import React, { useState } from 'react';
import { ChevronLeft, Plus, Trash2, Calculator, FileText } from 'lucide-react';
import type { Invoice, InvoiceItem } from '../../lib/database.types';
import { useCustomers, useWorkOrders } from '../../lib/hooks';

interface CreateInvoiceProps {
  onClose: () => void;
  onSubmit: (invoice: Partial<Invoice>, items: Partial<InvoiceItem>[]) => Promise<void>;
}

const CreateInvoice: React.FC<CreateInvoiceProps> = ({ onClose, onSubmit }) => {
  const { customers } = useCustomers();
  const { workOrders } = useWorkOrders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<string>('');
  const [items, setItems] = useState<Partial<InvoiceItem>[]>([
    {
      description: '',
      quantity: 1,
      unit_price: 0,
      amount: 0,
      tax_rate: 0,
      tax_amount: 0,
      type: 'service'
    }
  ]);

  const [invoiceData, setInvoiceData] = useState<Partial<Invoice>>({
    status: 'draft',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tax_rate: 0,
    notes: '',
    terms: 'Net 30',
  });

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const total = subtotal + taxAmount;

    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total,
      balance: total
    }));
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };

    if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
      const quantity = field === 'quantity' ? value : (newItems[index].quantity || 0);
      const unitPrice = field === 'unit_price' ? value : (newItems[index].unit_price || 0);
      const taxRate = field === 'tax_rate' ? value : (newItems[index].tax_rate || 0);
      
      const amount = quantity * unitPrice;
      const taxAmount = amount * (taxRate / 100);
      
      newItems[index] = {
        ...newItems[index],
        amount,
        tax_amount: taxAmount
      };
    }

    setItems(newItems);
    calculateTotals();
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unit_price: 0,
        amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        type: 'service'
      }
    ]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    calculateTotals();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      if (items.length === 0) {
        throw new Error('Please add at least one item');
      }

      if (items.some(item => !item.description || !item.quantity || !item.unit_price)) {
        throw new Error('Please fill in all item details');
      }

      const invoice: Partial<Invoice> = {
        ...invoiceData,
        customer_id: selectedCustomer,
        work_order_id: selectedWorkOrder || undefined
      };

      await onSubmit(invoice, items);
      onClose();
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Invoice</h1>
            <p className="text-gray-600">Create a new invoice for your customer</p>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <FileText className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="col-span-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Customer Information</h2>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer
                      </label>
                      <select
                        value={selectedCustomer}
                        onChange={(e) => setSelectedCustomer(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select a customer</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.first_name} {customer.last_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Work Order (Optional)
                      </label>
                      <select
                        value={selectedWorkOrder}
                        onChange={(e) => setSelectedWorkOrder(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select a work order</option>
                        {workOrders
                          .filter(wo => wo.customer_id === selectedCustomer)
                          .map((wo) => (
                            <option key={wo.id} value={wo.id}>
                              {wo.work_order_number} - {wo.service_type}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-medium text-gray-900">Items</h2>
                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tax Rate (%)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tax Amount
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Item description"
                                required
                              />
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={item.type}
                                onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="service">Service</option>
                                <option value="part">Part</option>
                                <option value="labor">Labor</option>
                                <option value="fee">Fee</option>
                                <option value="discount">Discount</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                                className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                min="0"
                                step="1"
                                required
                              />
                            </td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                                className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                value={item.tax_rate}
                                onChange={(e) => handleItemChange(index, 'tax_rate', parseFloat(e.target.value))}
                                className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                min="0"
                                max="100"
                                step="0.1"
                              />
                            </td>
                            <td className="px-6 py-4 text-gray-900">
                              ${item.amount?.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-gray-900">
                              ${item.tax_amount?.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-900"
                                disabled={items.length === 1}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Additional Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={invoiceData.notes}
                        onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Add any notes or special instructions..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Terms and Conditions
                      </label>
                      <textarea
                        value={invoiceData.terms}
                        onChange={(e) => setInvoiceData({ ...invoiceData, terms: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter terms and conditions..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Summary Sidebar */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden sticky top-8">
              <div className="p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-6">Invoice Summary</h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900 font-medium">
                      ${invoiceData.subtotal?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600">Tax</span>
                    <span className="text-gray-900 font-medium">
                      ${invoiceData.tax_amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2">
                    <span className="text-lg font-medium text-gray-900">Total</span>
                    <span className="text-xl font-bold text-blue-600">
                      ${invoiceData.total?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    {loading ? 'Creating...' : 'Create Invoice'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoice